import axios from "axios";

export interface UserPayload {
  sub: string;
  name: string;
  email: string;
}

export type MostJwtIssuer = "anketa";
export type MostJwtAudience = "opsanio-deployment-service";
export type MostJwtPurpose = "environment_deployment";
export type MostJwtType = "most_token";
export interface MostJwt extends UserPayload {
  iss: MostJwtIssuer;
  aud: MostJwtAudience;
  iat: number;
  exp: number;
  questionnaire: string;
  catalog: string;
  purpose: MostJwtPurpose;
  scope: string;
  type: MostJwtType;
  expiresIn: number;
  signature?: string;
  accessToken: string;
}

const SERVER_URL = "/api/";
//const SERVER_URL = "http://localhost:3011/api/";

export const signJWT = async (
  data: string,
  secret: string,
): Promise<string> => {
  try {
    const encoder = new TextEncoder();

    const key = await crypto.subtle.importKey(
      "raw",
      encoder.encode(secret),
      { name: "HMAC", hash: "SHA-256" },
      false,
      ["sign"],
    );
    const signature = await crypto.subtle.sign(
      "HMAC",
      key,
      encoder.encode(data),
    );
    const base64Signature = btoa(
      String.fromCharCode(...new Uint8Array(signature)),
    )
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=/g, "");

    return base64Signature;
  } catch (error) {
    console.error("💥 Error signing JWT:", error);
    throw new Error(`JWT signing failed: ${error.message}`);
  }
};

export const generateJWTToken = async (
  questionnaire: string,
  catalog: string,
  expiresIn: number = 3600,
  token: string,
): Promise<string> => {
  if (questionnaire === undefined || catalog === undefined) return;

  // Finding 3/6: the most-token is signed SERVER-SIDE. The browser never holds
  // the HMAC JWT_SECRET; we just ask the server to mint the token for us. The
  // `expiresIn` lifetime is fixed server-side (defaultJwtExpiresIn).
  try {
    const response = await axios.post(
      `${SERVER_URL}v2/questionnaires/${catalog}/${questionnaire}/most-token`,
      {},
      { headers: { Authorization: `Bearer ${token}` } },
    );

    const minted = response.data?.token;
    if (!minted || typeof minted !== "string") {
      throw new Error("Server did not return a most token");
    }
    return minted;
  } catch (error) {
    console.error("💥 Critical error generating JWT token:", error);
    throw new Error(
      `Failed to generate JWT token: ${error.response?.data || error.message}`,
    );
  }
};

export const generateSimpleToken = async (
  questionnaire: string,
  catalog: string,
  expiresIn: number = 3600,
  env: string,
  token: string,
): Promise<string> => {
  const now = Math.floor(Date.now() / 1000);

  let email: string = "string";
  let accessToken: string = null;
  let name: string = "string";

  try {
    const tokenResponse = await axios.get(
      `${SERVER_URL}v2/questionnaires/${catalog}/${questionnaire}/token`,
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );

    email = tokenResponse.data.email || email;
    accessToken = tokenResponse.data.token;
  } catch (err) {
    console.error(
      "❌ Error getting Access Token for Simple Token:",
      err.response?.data || err.message,
    );
  }
  try {
    const metadataResponse = await axios.get(
      `${SERVER_URL}v2/questionnaires/${catalog}/${questionnaire}/metadata`,
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );

    name = metadataResponse.data.name || name;
  } catch (err) {
    console.error(
      "❌ Error getting Metadata for Simple Token:",
      err.response?.data || err.message,
    );
  }

  const signatureData = `${questionnaire}-${catalog}-${now}-${expiresIn}-${env}`;

  let signature: string;
  try {
    if (typeof crypto !== "undefined" && crypto.subtle) {
      const encoder = new TextEncoder();
      const data = encoder.encode(signatureData);
      const hashBuffer = await crypto.subtle.digest("SHA-256", data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      signature = btoa(String.fromCharCode.apply(null, hashArray)).slice(0, 32);
    } else {
      signature = btoa(signatureData).slice(0, 32);
    }
  } catch (error) {
    console.error(
      "❌ Error generating crypto signature, falling back to base64:",
      error,
    );
    signature = btoa(signatureData).slice(0, 32);
  }

  const payload: MostJwt = {
    sub: questionnaire,
    name,
    email,
    accessToken,

    iss: "anketa",
    aud: "opsanio-deployment-service",
    iat: now,
    exp: now + expiresIn,

    questionnaire,
    catalog,
    purpose: "environment_deployment",
    scope: `deployment:${env}`,
    type: "most_token",
    expiresIn,
    signature,
  };

  const encodedToken = btoa(JSON.stringify(payload));

  return encodedToken;
};

export const decodeMostToken = (token: string): MostJwt => {
  try {
    const parts = token.split(".");
    if (parts.length === 3) {
      const payload = parts[1];
      const decoded = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
      return JSON.parse(decoded);
    } else {
      const decoded = atob(token);
      return JSON.parse(decoded);
    }
  } catch (error) {
    console.error("Token decoding failed:", error);
    return null;
  }
};

export const timeToSeconds = {
  minutes: (min: number) => min * 60,
  hours: (hrs: number) => hrs * 3600,
  days: (days: number) => days * 86400,
  weeks: (weeks: number) => weeks * 604800,
};

export const convertSeconds = (seconds: number): string => {
  if (seconds < 0) return "Invalid input (negative time)";
  if (seconds === 0) return "0 seconds";

  const units: { name: string; seconds: number }[] = [
    { name: "year", seconds: 60 * 60 * 24 * 365 },
    { name: "month", seconds: 60 * 60 * 24 * 30 },
    { name: "week", seconds: 60 * 60 * 24 * 7 },
    { name: "day", seconds: 60 * 60 * 24 },
    { name: "hour", seconds: 60 * 60 },
    { name: "minute", seconds: 60 },
    { name: "second", seconds: 1 },
  ];

  const parts: string[] = [];
  let remainingSeconds = seconds;

  for (const unit of units) {
    if (remainingSeconds >= unit.seconds) {
      const count = Math.floor(remainingSeconds / unit.seconds);
      remainingSeconds %= unit.seconds;
      const plural = count !== 1 ? "s" : "";
      parts.push(`${count} ${unit.name}${plural}`);
    }
  }

  return parts.join(", ");
};

export const verifyUserPayloadInToken = (token: string): boolean => {
  const decoded = decodeMostToken(token);
  if (!decoded) return false;

  const requiredFields = ["sub", "name", "accessToken"];
  return requiredFields.every((field) => decoded.hasOwnProperty(field));
};

export const verifyJWT = async (
  data: string,
  signature: string,
  secret: string,
): Promise<boolean> => {
  try {
    const encoder = new TextEncoder();

    const key = await crypto.subtle.importKey(
      "raw",
      encoder.encode(secret),
      { name: "HMAC", hash: "SHA-256" },
      false,
      ["verify"],
    );
    const binarySignature = new Uint8Array(
      atob(signature.replace(/-/g, "+").replace(/_/g, "/"))
        .split("")
        .map((c) => c.charCodeAt(0)),
    );
    const isValid = await crypto.subtle.verify(
      "HMAC",
      key,
      binarySignature,
      encoder.encode(data),
    );

    return isValid;
  } catch (error) {
    console.error("💥 Error verifying JWT signature:", error);
    return false;
  }
};
